import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Copy, Check } from "lucide-react";
import { applyForAudience } from "@/lib/api";

const ease = [0.22, 1, 0.36, 1];

const QUESTIONS = [
  {
    key: "vision",
    label: "I.  The Vision",
    prompt:
      "When you imagine matriarchal power fully realised in your field — what does it look like, and what did it cost the women before you?",
  },
  {
    key: "lineage",
    label: "II.  The Lineage",
    prompt:
      "Name a pattern in your bloodline you intend to end, and the inheritance you intend to begin.",
  },
  {
    key: "dismantle",
    label: "III.  The Dismantling",
    prompt:
      "What patriarchal system are you actively dismantling, and what have you already risked to do so?",
  },
];

export const Mysteries = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", vision: "", lineage: "", dismantle: "" });
  const [loading, setLoading] = useState(false);
  const [granted, setGranted] = useState(null);
  const [copied, setCopied] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Your name and a means of contact are required.");
      return;
    }
    for (const q of QUESTIONS) {
      if (form[q.key].trim().length < 10) {
        toast.error(`The assessment demands a fuller answer — ${q.label.split(".")[1].trim()}.`);
        return;
      }
    }
    setLoading(true);
    try {
      const data = await applyForAudience({
        name: form.name.trim(),
        email: form.email.trim(),
        vision: form.vision.trim(),
        lineage: form.lineage.trim(),
        dismantle: form.dismantle.trim(),
      });
      setGranted(data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "The assessment was not accepted.");
    } finally {
      setLoading(false);
    }
  };

  const copySigil = async () => {
    try {
      await navigator.clipboard.writeText(granted.sigil);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed — note it by hand.");
    }
  };

  return (
    <div className="relative min-h-screen w-full bg-[#040404] py-20" data-testid="mysteries-page">
      <div className="pointer-events-none absolute left-1/2 top-0 h-[60vh] w-[60vh] -translate-x-1/2 rounded-full bg-[#3B2A48]/15 blur-[130px]" />

      <div className="relative mx-auto max-w-2xl px-6">
        <Link
          to="/gate"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/40 transition-colors hover:text-[#C5A059]"
          data-testid="mysteries-back-link"
        >
          <ArrowLeft size={15} strokeWidth={1.5} /> Back to the Gate
        </Link>

        <AnimatePresence mode="wait">
          {!granted ? (
            <motion.div
              key="form"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.8, ease }}
              className="mt-12"
            >
              <p className="overline mb-5">The Mysteries — The Sybils Assessment</p>
              <h1 className="font-serif text-[clamp(2.2rem,6vw,3.75rem)] font-light leading-[1.02] tracking-tight text-white">
                Request an <span className="italic text-[#C5A059]">Audience</span>.
              </h1>
              <p className="mt-5 max-w-lg text-sm font-light leading-relaxed text-white/50">
                This is not a form. It is a reckoning. Answer with your whole self —
                not everyone is accepted. Those who are will be granted a provisional Sigil.
              </p>

              <form onSubmit={submit} className="mt-12 space-y-8">
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <input
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    placeholder="Your name"
                    className="rounded-lg border border-[#C5A059]/25 bg-white/5 px-4 py-3 text-sm font-light text-white placeholder:text-white/30 outline-none transition-colors focus:border-[#C5A059]/70"
                    data-testid="mysteries-name-input"
                  />
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    placeholder="Your email"
                    className="rounded-lg border border-[#C5A059]/25 bg-white/5 px-4 py-3 text-sm font-light text-white placeholder:text-white/30 outline-none transition-colors focus:border-[#C5A059]/70"
                    data-testid="mysteries-email-input"
                  />
                </div>

                {QUESTIONS.map((q) => (
                  <div key={q.key}>
                    <label className="mb-3 block font-mono text-xs uppercase tracking-[0.2em] text-[#C5A059]/80">
                      {q.label}
                    </label>
                    <p className="mb-3 font-serif text-lg font-light italic leading-snug text-white/70">
                      {q.prompt}
                    </p>
                    <textarea
                      value={form[q.key]}
                      onChange={(e) => update(q.key, e.target.value)}
                      rows={4}
                      placeholder="Speak plainly…"
                      className="w-full resize-none rounded-lg border border-[#C5A059]/25 bg-white/5 px-4 py-3 text-sm font-light leading-relaxed text-white placeholder:text-white/25 outline-none transition-colors focus:border-[#C5A059]/70"
                      data-testid={`mysteries-${q.key}-input`}
                    />
                  </div>
                ))}

                <button
                  type="submit"
                  disabled={loading}
                  className="cta-glow w-full rounded-full bg-[#C5A059] py-4 text-xs font-medium uppercase tracking-[0.26em] text-[#050505] transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
                  data-testid="mysteries-submit-btn"
                >
                  {loading ? "The Oracles deliberate…" : "Submit to the Assessment"}
                </button>
              </form>
            </motion.div>
          ) : (
            <motion.div
              key="granted"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, ease }}
              className="mt-24 flex flex-col items-center text-center"
              data-testid="mysteries-granted"
            >
              <p className="overline mb-5">Provisional Passage Granted</p>
              <h2 className="font-serif text-[clamp(2rem,6vw,3.5rem)] font-light leading-tight tracking-tight text-white">
                Welcome, <span className="italic text-[#C5A059]">{granted.name}</span>.
              </h2>
              <p className="mt-4 max-w-md text-sm font-light leading-relaxed text-white/50">
                You enter as <span className="text-[#C5A059]">{granted.tier}</span>. Guard
                this Sigil — it is spoken only once. Present it at the Gate.
              </p>

              <button
                onClick={copySigil}
                className="glass mt-10 flex items-center gap-4 rounded-full px-8 py-5 font-mono text-base tracking-[0.25em] text-[#C5A059] transition-colors hover:border-[#C5A059]/60"
                data-testid="mysteries-sigil-value"
              >
                {granted.sigil}
                {copied ? <Check size={16} /> : <Copy size={16} strokeWidth={1.5} />}
              </button>

              <button
                onClick={() => navigate("/gate")}
                className="cta-glow mt-10 rounded-full bg-[#C5A059] px-10 py-4 text-xs font-medium uppercase tracking-[0.26em] text-[#050505] transition-transform duration-300 hover:-translate-y-0.5"
                data-testid="mysteries-proceed-gate-btn"
              >
                Take this Sigil to the Gate
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Mysteries;
